import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useRouter } from 'next/router';
import { FC, useEffect } from 'react';
import { Url } from 'url';

import { sessionQueryOptions } from '@/queries/upgrade/sessions';

interface IAuthRedirectProps {
  /**
   * URL used to redirect a user to
   * By default, if only this prop is set, the component will redirect if no session is found
   */
  redirectTo: Url | string;
  /**
   * If this property is set to `true`, user will be redirected if he is logged in.
   * Useful when you don't want to show login page to already logged in users.
   */
  ifFound?: boolean;
}

export const AuthRedirect: FC<IAuthRedirectProps> = ({ redirectTo, ifFound }) => {
  const { data, isLoading, error } = useQuery(sessionQueryOptions());
  const router = useRouter();

  const user = data?.user;

  useEffect(() => {
    // if no redirect needed, just return (example: already on /dashboard)
    // if user data not yet there (fetch in progress, logged in or not) then don't do anything yet
    if (!redirectTo || isLoading) return;

    const isLoggedOut = !user && (!error || (isAxiosError(error) && error.response?.status === 401));
    const shouldRedirect = (ifFound && user) || (!ifFound && isLoggedOut);

    if (shouldRedirect) {
      router.push(redirectTo);
    }
  }, [user, ifFound, redirectTo, error, isLoading, router]);

  // this component renders nothing since it is only used to redirect if needed.
  return null;
};
